import { createContext, useContext, useMemo } from 'react'

import { SquareupCommonCountries } from '@cash-web/shared-util-protos'

export type Region = `${SquareupCommonCountries.Country}`

const RegionContext = createContext<{ region: Region; supportedRegions?: Region[] } | undefined>(undefined)

type ReactProviderProps = {
  region: Region
  children?: React.ReactNode
}

// The RegionProvider should never be imported by apps or other libs
// except to be used in tests. If a RegionProvider is needed, a SupportedRegionProvider
// should be created with `makeSupportedRegionProvider`.
export function RegionProvider({ region, children }: ReactProviderProps): React.ReactElement {
  const value = useMemo(
    () => ({
      region,
    }),
    [region]
  )

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>
}

// `useRegion` should only be used in cases where the app's supported regions
// aren't known (like in common libs) or where any region is supported. If the set of supported regions is known,
// a hook should probably be created with `makeSupportedRegionProvider`.
export function useRegion(): Region {
  const context = useContext(RegionContext)

  if (!context) {
    throw Error('useRegion must be used within a RegionProvider')
  }

  return context.region
}
